import type { ReactNode } from 'react';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import styles from './BulkBar.module.css';

export interface BulkBarProps {
  count: number;
  /** The actions this reader may take on the selection, as buttons. */
  children: ReactNode;
  onClear: () => void;
  /**
   * Ticks every case on the page, offered while some on it are not ticked. The
   * header's box does the same above a phone's width, where there is a header.
   */
  onSelectPage?: () => void;
}

/**
 * What a selection can have done to it, under the table it was made on.
 * Section 4 of the request of 24 September 2026.
 *
 * Only while something is ticked: an empty bar would be furniture on every list
 * in the lab. It says how many, carries the actions, and clears the selection.
 * Sticky to the foot of the screen, so it stays in reach while the reader
 * scrolls a long page ticking cases, and after the table, so appearing on the
 * first tick it moves no row out from under the pointer.
 */
export function BulkBar({ count, children, onClear, onSelectPage }: BulkBarProps) {
  return (
    <div className={styles.bar} role="region" aria-label={strings.bulk.barLabel}>
      <span className={styles.count} aria-live="polite">
        <b>{count}</b> {count === 1 ? strings.bulk.selectedOne : strings.bulk.selectedMany}
      </span>
      {onSelectPage !== undefined && (
        <Button variant="text" onClick={onSelectPage}>
          {strings.bulk.pageToo}
        </Button>
      )}
      <span className={styles.spacer} />
      <div className={styles.actions}>{children}</div>
      <Button variant="text" onClick={onClear}>
        {strings.bulk.clear}
      </Button>
    </div>
  );
}
